import type { ReactNode } from 'react';
import type { AppSnapshot } from '../../app/state';
import { callAction } from '../../app/actions';
import { todoText } from '../../i18n/todos';

/**
 * ONE heading for every section of the "My items" pane (V0.50).
 *
 * The pane had four flat headings and no way to shorten any of them; V0.50 adds a fifth, and
 * four lists that cannot be folded is where a sidebar stops working. So every heading gets the
 * same control, from the same component — a second copy of a fold chevron is precisely the
 * failure mode this codebase has paid for before.
 *
 * WHAT IT IS NOT: this is not the tag panel's fold. That one is SHARED — `tags[id].collapsed`
 * is tag structure and syncs to the server, so one person folding a tag group folds it for
 * everybody. `CLAUDE.md` names that a mistake not to repeat, so this one is per user and local:
 * `S.collapsedSections`, written into the day-scoped VIEWSTORE record. It expires at 04:00 with
 * the rest of that record, which is right — the map's own branches work exactly that way, and a
 * sidebar folded shut three weeks ago is not a preference anybody still remembers making.
 *
 * `trailing` is what the heading carries on its right BEFORE the chevron — today only the My
 * assignments eye. It sits inside the heading rather than beside it so the two controls share
 * one baseline and one hover target row.
 */
export function SectionHeading({
  sectionKey,
  label,
  s,
  trailing,
}: {
  /** The key in `S.collapsedSections`. Owned by the caller; nothing central lists them. */
  sectionKey: string;
  label: string;
  s: AppSnapshot;
  trailing?: ReactNode;
}) {
  const folded = s.collapsedSections.has(sectionKey);
  return (
    <div className="myitems-title myitems-title-flex" data-section={sectionKey}>
      <span>{label}</span>
      {trailing}
      <button
        type="button"
        className="myitems-fold"
        aria-expanded={!folded}
        aria-label={todoText(folded ? 'expand' : 'collapse', s.currentLocale)}
        title={todoText(folded ? 'expand' : 'collapse', s.currentLocale)}
        onClick={() => callAction('setSectionCollapsed', sectionKey, !folded)}
      >
        <svg className={folded ? 'pp-fold' : 'pp-fold open'} viewBox="0 0 24 24" aria-hidden="true">
          <use href="#i-chev" />
        </svg>
      </button>
    </div>
  );
}
